import _ from "lodash";
import React, { useContext, useState } from "react";
import { useEffect } from "react";
import { createUseStyles } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Placeholder } from "rsuite";
import { preBook, preBookUpdate } from "../../../api/activities";
import { useApoAuth, useBaseReservationPayload } from "../../../hooks";
import {
  CurrencyContext,
  StyleTypeContext,
  TranslationContext,
} from "../../../umbilical_tools";
import Prebook from "../Prebook";
import TravellersForms from "../TravellersForms";
import PropTypes from "prop-types";
import {
  createPrebookUpdatePayload,
  getData,
  initActivityPayment,
  initGuestData,
  initGuestFormStructure,
  onCreateActivityBooking,
} from "./helpers";
import { toast } from "react-toastify";
import { activitiesErrorMsgs } from "../../../errors";
import update from "immutability-helper";
import { validateActivityGuests } from "../../../validators";
import BarclaysPayment from "../../common/payment/barclays";
import ExternallyHostedPayment from "../../common/StripeHostedPayment";
import { Button } from "../../common/Button";
import Stepper from "../../common/Stepper";
import jssVariables from "../../../jss_setup";
import { useBookingProcess } from "../../../hooks/booking";
import { getPriceDecimalsSelector } from "../../../selectors";
import { gaussianRounding } from "../../../umbilical_hocs";

const styles = createUseStyles({
  bookingProcess: (props) => ({
    position: "fixed",
    height: "100%",
    width: "100vw",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    gridGap: jssVariables.normal_gap,
    background: jssVariables.colors.background.light,
    padding: jssVariables.normal_gap,
    justifyContent: "center",
    zIndex: 9999,
    ..._.get(props, "customStyles.bookingProcess", {}),
  }),
  stepper: (props) => ({
    ..._.get(props, "customStyles.stepper", {}),
  }),
  content: (props) => ({
    boxShadow: jssVariables.shadows.normalShadow,
    borderRadius: jssVariables.border_radius,
    padding: `calc(${jssVariables.normal_gap} * 2)`,
    height: "max-content",
    maxHeight: "90vh",
    minWidth: "90vw",
    ..._.get(props, "customStyles.content", {}),
  }),
  actions: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    justifyContent: "end",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.actions", {}),
  }),
  placeholderBtn: (props) => ({
    height: "2rem!important",
    width: "6rem!important",
    ..._.get(props, "customStyles.placeholderBtn", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    content: (props) => ({
      minWidth: "30rem",
      maxWidth: "50rem",
      height: "max-content",
      maxHeight: "85vh",
      marginTop: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
const ActivityBookingProcess = ({ page_session_id, prebook }) => {
  const {
    payment_supplier,
    loading,
    step,
    onIncreaseStep,
    onDecreaseStep,
    onLoading,
    onIdle,
  } = useBookingProcess({ initialLoading: true });

  const [optsForDisplay, setOptsForDisplay] = useState({});
  const [forms, setForms] = useState({});
  const [leadPartFormStruct, setLeadPartFormStruct] = useState({});
  const [guests, setGuests] = useState({});
  const [requiredData, setRequiredData] = useState({});
  const [prebookData, setPrebookData] = useState({});
  const [agreed, setAgreed] = useState(false);

  const [prebookUpdateData, setPrebookUpdateData] = useState({});
  const [bookLoading, setBookLoading] = useState(true);
  const priceDecimals = useSelector((state) => getPriceDecimalsSelector(state));

  const { source_entity } = useApoAuth();
  const { website_name } = useSelector((state) => ({
    website_name: state.WebsiteMetadata?.metadata?.name,
  }));

  const { t } = useContext(TranslationContext);
  const { currency: settedCurrency } = useContext(CurrencyContext);

  const history = useHistory();
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "ActivityBookingProcess",
  });
  const classes = styles({ customStyles });

  const { activity, search_session_id, search_destination_meta } = useSelector(
    (state) => {
      return {
        activity: _.get(state.ActivitySelected, "activity", {}),
        search_session_id: _.get(
          state.ActivitySearchForm,
          "search_session_id",
          ""
        ),
        search_destination_meta: _.get(
          state,
          "ActivitySearchForm.destination_meta",
          {}
        ),
      };
    }
  );

  const dispatch = useDispatch();

  function onCancel() {
    var activity_uid = activity.uid
      ? activity.uid
      : _.get(
          getData({ prebook, search_session_id, history }),
          "activity_uid",
          ""
        );

    history.push({
      pathname: `/activity_details/${page_session_id}`,
      search: `?activity_uid=${activity_uid}`,
    });
  }

  async function onPreBook({ data }) {
    const result = await preBook({
      data,
    });

    if (!result) {
      toast.warning(activitiesErrorMsgs.general.no_session, {
        duration: 3000,
      });
      onCancel();
      return;
    }
    setPrebookData(result);
    const { guests: tmpGuests, leadParticipantFormStructure } = initGuestData({
      result,
    });
    const tmpFormStruct = initGuestFormStructure({ result, guests: tmpGuests });
    setRequiredData({
      customer_data_required: result.customer_data_required,
      participants_data_required: result.participants_data_required,
    });

    setGuests(tmpGuests);
    setForms(tmpFormStruct);
    setLeadPartFormStruct(leadParticipantFormStructure);
    onIdle();
  }

  async function onPreBookUpdate() {
    const payload = createPrebookUpdatePayload({
      guests,
      activityUid: activity.uid,
      requiredData,
      leadPartFormStruct,
    });
    const result = await preBookUpdate({ payload, search_session_id });
    if (!result) {
      toast.warning(activitiesErrorMsgs.general.no_session, { duration: 3000 });
      onCancel();
      onDecreaseStep();
      return;
    }
    setPrebookUpdateData(result);
    setBookLoading(false);
  }

  const {
    first_name,
    last_name,
    email,
    customer_uid,
    nationality,
    customer_stripe_id,
  } = useBaseReservationPayload({ group_leader: _.get(guests, "0.form", {}) });

  // Prebook
  useEffect(() => {
    if (!search_session_id) {
      history.push({ pathname: `/activities/${page_session_id}` });
      return;
    }
    const decodedData = getData({ prebook, search_session_id, history });

    if (!_.isEmpty(decodedData) && search_session_id) {
      onLoading();
      setOptsForDisplay({ ...decodedData });
      onPreBook({ data: decodedData });
    } else {
      onCancel();
    }
  }, [search_session_id]);

  // Prebook Update
  useEffect(() => {
    if (step !== 2) {
      return;
    }

    if (!validateActivityGuests({ guests, forms, t })) {
      toast.warning(activitiesErrorMsgs.guestValidation.init_guests_fail, {
        duration: 3000,
      });
      onDecreaseStep();
      return;
    }

    onPreBookUpdate();
  }, [step]);

  // Book & Pay
  useEffect(() => {
    if (step !== 3) {
      return;
    }
    const customer_user_data = {
      customer_uid,
      email,
      first_name,
      last_name,
      nationality,
    };

    onCreateActivityBooking({
      prebookData,
      prebookUpdateData,
      session_id: search_session_id,
      guests,
      activity,
      customer_user_data,
      website_name,
      destination_meta: search_destination_meta,
      page_session_id,
      settedCurrency,
      customer_stripe_id,
      source_entity,
      dispatch,
      history,
    }).then((result) => {
      if (!result) return;
      var { booking_reference, amount, currency } = result;

      amount = gaussianRounding(amount, priceDecimals);

      const title = prebookUpdateData?.title ?? "";
      const operation_datetime = prebookUpdateData.datetime;

      initActivityPayment({
        title,
        operation_datetime,
        booking_reference,
        amount,
        currency,
        page_session_id,
        guests,
        settedCurrency,
        activity_uid: activity.uid,
        customer_stripe_id,
        customer_uid,
        email,
        first_name,
        last_name,
        nationality,
        source_entity,
        dispatch,
      });
    });
  }, [step]);

  function getTime() {
    const array = _.get(prebookUpdateData, "cart_items.0.date", "").split(" ");
    return array.length === 2 ? array[1] : "";
  }

  return (
    <div className={classes.bookingProcess}>
      <div className={classes.stepper}>
        <Stepper
          steps={[t("prebook"), t("guests"), t("payment")]}
          current={step === 4 ? 2 : step}
        />
      </div>
      <div className={classes.content}>
        {step === 0 && (
          <Prebook
            loading={loading}
            optsForDisplay={optsForDisplay}
            agreed={agreed}
            setAgreed={setAgreed}
          />
        )}
        {step === 1 && (
          <div className={classes.guestForms}>
            <TravellersForms
              forms={forms}
              optsForDisplay={optsForDisplay}
              guests={guests}
              setGuests={(guest_uuid, values) => {
                const index = guests.findIndex(
                  (guest) => guest.guest_uuid === guest_uuid
                );
                setGuests((p) =>
                  update(p, {
                    [index]: {
                      form: { $set: { ...values } },
                    },
                  })
                );
              }}
            />
          </div>
        )}
        {step === 2 && (
          // prebook with cancelation policy
          <Prebook
            loading={bookLoading}
            optsForDisplay={optsForDisplay}
            time={getTime()}
            prebookUpdateData={prebookUpdateData}
            agreed={agreed}
            setAgreed={setAgreed}
          />
        )}
        {step === 3 && payment_supplier !== "BCR" && (
          <ExternallyHostedPayment />
        )}
        {step === 3 && payment_supplier === "BCR" && <BarclaysPayment />}
        <div className={classes.actions}>
          {step <= 2 && (
            <React.Fragment>
              {loading ? (
                <React.Fragment>
                  <Placeholder.Graph
                    active
                    className={classes.placeholderBtn}
                  />
                  <Placeholder.Graph
                    active
                    className={classes.placeholderBtn}
                  />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Button
                    id={step > 0 ? "previousStep" : "cancel"}
                    text={step > 0 ? t("previousStep") : t("cancel")}
                    bold={true}
                    size="small"
                    onClick={() => (step > 0 ? onDecreaseStep() : onCancel())}
                  />
                  <Button
                    id={step < 2 ? "nextStep" : "payment"}
                    text={step < 2 ? t("nextStep") : t("payment")}
                    bold={true}
                    size="small"
                    onClick={() => {
                      if (step === 0 && !agreed) {
                        toast.warning(t("pleaseCheckTheBox"));
                        return;
                      }
                      onIncreaseStep();
                    }}
                  />
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  );
};
ActivityBookingProcess.defaultProps = {
  page_session_id: false,
  prebook: "",
};
ActivityBookingProcess.propTypes = {
  page_session_id: PropTypes.string.isRequired,
  prebook: PropTypes.string.isRequired,
};
export default ActivityBookingProcess;
